import { z } from 'zod';

/**
 * Compliance documents for trucks, trailers and drivers (PRD → Dispatch and
 * eligibility → Compliance; gitaumoses4/chukua#17): one shared shape across
 * all three holders. A renewal is a new row, never an edit of the one it
 * supersedes — there is no update schema here on purpose.
 */

export const HOLDER_KINDS = ['truck', 'trailer', 'driver'] as const;
export type HolderKind = (typeof HOLDER_KINDS)[number];

/** 'missing' only applies to an active document type with no current row for the holder — never stored, always derived at read time. */
export const DOCUMENT_STATUSES = ['valid', 'expiring_soon', 'expired', 'missing'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

/** Presigned-upload allow-list: a scanned certificate or a photo of one, nothing else. */
export const COMPLIANCE_DOCUMENT_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export type ComplianceDocumentContentType = (typeof COMPLIANCE_DOCUMENT_CONTENT_TYPES)[number];

/** 15 MB — the larger of the two limits this replaces (trucks' 15 MB, drivers' 10 MB); the ticket doesn't ask for per-holder caps. */
export const COMPLIANCE_DOCUMENT_MAX_BYTES = 15 * 1024 * 1024;

export const presignComplianceDocumentSchema = z.object({
  contentType: z.enum(COMPLIANCE_DOCUMENT_CONTENT_TYPES),
});
export type PresignComplianceDocumentInput = z.infer<typeof presignComplianceDocumentSchema>;

/** Add or renew — the service decides which audit verb applies by checking whether a current row already exists. */
export const attachComplianceDocumentSchema = z
  .object({
    documentTypeId: z.string().uuid(),
    number: z.string().trim().min(1).max(80),
    issuer: z.string().trim().min(1).max(160),
    issueDate: z.iso.date(),
    expiryDate: z.iso.date(),
    key: z.string().trim().min(1).max(300),
    contentType: z.enum(COMPLIANCE_DOCUMENT_CONTENT_TYPES),
  })
  .refine((value) => value.issueDate <= value.expiryDate, { message: 'issueDate must not be after expiryDate', path: ['issueDate'] });
export type AttachComplianceDocumentInput = z.infer<typeof attachComplianceDocumentSchema>;
